import { useState, useEffect } from 'preact/hooks';
import type { GuestSession } from '../api/types';
import {
  addSession,
  setActiveSessionId,
  clearAllSessions,
  updateSessionExpiry,
  type StoredSession,
} from '../lib/sessionStore';
import { ApiError, clearCsrfToken, guestApi } from '../api/client';

interface AuthState {
  isGuest: boolean;
  isAuthenticated: boolean;
  guestSession: GuestSession | null;
  accessToken: string | null;
}

export function useAuth(): AuthState & {
  setGuestSession: (session: GuestSession) => void;
  setAccessToken: (token: string) => void;
  logout: () => void;
} {
  const [state, setState] = useState<AuthState>({
    isGuest: false,
    isAuthenticated: false,
    guestSession: null,
    accessToken: null,
  });

  useEffect(() => {
    // Re-check a locally-lapsed session against the server, which is the only
    // party that knows whether it was renewed. Adopts the fresh expiresAt on
    // success; only a rejection (401/no session) actually clears local state.
    //
    // suppressAuthRedirect is what makes that contract keepable. The default 401
    // handling in rawFetch clears storage and hard-navigates to '/', which would
    // both bypass the branch below and throw away the deep link a guest opened.
    // Here the 401 is expected and diagnostic, so we handle it ourselves.
    async function refreshStoredSession(session: GuestSession): Promise<void> {
      try {
        const fresh = await guestApi.getSessionMetadata({ suppressAuthRedirect: true });
        if (!fresh?.expiresAt) return; // Inconclusive — leave local state alone.
        const renewed: GuestSession = { ...session, ...fresh };
        localStorage.setItem('guest_session', JSON.stringify(renewed));
        if (fresh.sessionId) {
          updateSessionExpiry(fresh.sessionId, fresh.expiresAt);
        }
        setState({
          isGuest: true,
          isAuthenticated: true,
          guestSession: renewed,
          accessToken: null,
        });
      } catch (err) {
        // Only the server saying "this session is gone" is grounds for eviction.
        // A 500, a rate limit, or an offline device says nothing about whether
        // the session is live, and clearing on those would strand exactly the
        // guest this refresh exists to rescue.
        if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
          clearCsrfToken();
          localStorage.removeItem('guest_session');
        }
      }
    }

    // Check for existing session in localStorage (persists across browser closes)
    const savedSession = localStorage.getItem('guest_session');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession) as GuestSession;
        if (new Date(session.expiresAt) > new Date()) {
          setState({
            isGuest: true,
            isAuthenticated: true,
            guestSession: session,
            accessToken: null,
          });
        } else {
          // The stored expiresAt is a snapshot from join time, but guest sessions
          // renew server-side as the guest keeps showing up. A locally-lapsed
          // snapshot therefore means "ask the server", not "log them out" —
          // evicting here would strand a guest whose session is in fact live.
          void refreshStoredSession(session);
        }
      } catch {
        localStorage.removeItem('guest_session');
      }
    }

    const savedToken = localStorage.getItem('access_token');
    if (savedToken) {
      setState((prev) => ({
        ...prev,
        isGuest: false,
        isAuthenticated: true,
        accessToken: savedToken,
      }));
    }
  }, []);

  const setGuestSession = (session: GuestSession) => {
    localStorage.setItem('guest_session', JSON.stringify(session));
    setState({
      isGuest: true,
      isAuthenticated: true,
      guestSession: session,
      accessToken: null,
    });

    // Also store in multi-session store for "My Stories". The secret sessionToken
    // is persisted here so the returning-guest resume flow can present it as
    // proof-of-possession (W4); accessScope keys the shared-device dedupe (W7).
    if (session.sessionId) {
      const stored: StoredSession = {
        sessionId: session.sessionId,
        sessionToken: session.sessionToken,
        spaceId: session.spaceId,
        spaceName: session.spaceName,
        threadId: session.threadId,
        accessScope: session.accessScope,
        displayName: session.displayName ?? '',
        expiresAt: session.expiresAt,
      };
      addSession(stored);
      setActiveSessionId(session.sessionId);
    }
  };

  const setAccessToken = (token: string) => {
    localStorage.setItem('access_token', token);
    localStorage.removeItem('guest_session');
    setState({
      isGuest: false,
      isAuthenticated: true,
      guestSession: null,
      accessToken: token,
    });
  };

  const logout = () => {
    // Best-effort server-side revoke so the session token can't be replayed
    // after logout (W7). Fire-and-forget — local state is cleared regardless.
    if (state.isGuest) {
      void guestApi.logout().catch(() => { /* ignore */ });
    }
    localStorage.removeItem('guest_session');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    clearAllSessions();
    clearCsrfToken();
    setState({
      isGuest: false,
      isAuthenticated: false,
      guestSession: null,
      accessToken: null,
    });
  };

  return { ...state, setGuestSession, setAccessToken, logout };
}
